import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useLogin } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { api } from '@/api/client';
import { Clock } from 'lucide-react';

interface StatusResponse {
  state: 'none' | 'pending' | 'active' | 'inactive';
  email?: string;
}

export function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user, isLoading } = useAuth();
  const { login } = useLogin();

  const error = searchParams.get('error');
  const pendingParam = searchParams.get('pending') === '1';

  // Poll /auth/status whenever we're sitting on ?pending=1 (or the backend
  // still has a pending cookie). When it flips to 'active' the backend has
  // already minted the session cookie, so we just refetch /auth/me and route
  // to the dashboard.
  const { data: statusData } = useQuery<StatusResponse>({
    queryKey: ['auth', 'status'],
    queryFn: () => api.get<StatusResponse>('/auth/status'),
    enabled: !user && !isLoading,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      // Only keep polling while we're actually pending
      return state === 'pending' ? 5000 : false;
    },
    refetchIntervalInBackground: false,
    retry: false,
  });

  const isPending = pendingParam || statusData?.state === 'pending';
  const isInactive = statusData?.state === 'inactive' || error === 'account_inactive';

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // When the status poll reports 'active', the server has already swapped in
  // an auth_token cookie — refetch /auth/me so ProtectedRoute lets us in.
  useEffect(() => {
    if (statusData?.state === 'active') {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  }, [statusData?.state, queryClient]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-xtravirt-dark via-xtravirt-navy to-xtravirt-cyan">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-xtravirt-dark p-4">
      <div className="w-full max-w-sm">
        {/* Login Card */}
        <div className="rounded-lg bg-white p-8 shadow-xl">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img src="/xtravirt-logo.png" alt="Xtravirt" className="h-8" />
          </div>

          {/* Title */}
          <h1 className="text-center font-heading text-xl font-bold text-xtravirt-dark">
            Professional Services Tool
          </h1>
          <p className="mt-1.5 text-center text-sm text-gray-500">Scoping & Pipeline Management</p>

          {/* Pending approval state */}
          {isPending && !isInactive && (
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <Clock className="h-5 w-5 text-amber-700" />
              </div>
              <p className="font-semibold text-amber-900">Awaiting approval</p>
              <p className="mt-1 text-xs text-amber-800">
                {statusData?.email
                  ? `${statusData.email} — thanks for signing in.`
                  : 'Thanks for signing in.'}
                <br />
                An administrator needs to approve your account and assign you to a group.
              </p>
              <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-amber-700">
                <LoadingSpinner size="sm" />
                <span>Checking every few seconds — this page will refresh automatically.</span>
              </div>
            </div>
          )}

          {/* Inactive account */}
          {isInactive && (
            <div className="mt-6 rounded border border-red-200 bg-red-50 p-3">
              <p className="text-center text-sm text-red-700">
                Your account has been deactivated. Please contact an administrator.
              </p>
            </div>
          )}

          {/* Error message */}
          {error && !isInactive && (
            <div className="mt-4 rounded border border-red-200 bg-red-50 p-3">
              <p className="text-center text-sm text-red-700">
                {error === 'invalid_state'
                  ? 'Login session expired. Please try again.'
                  : error === 'oauth_failed'
                    ? 'Authentication failed. Please try again.'
                    : `Error: ${error}`}
              </p>
            </div>
          )}

          {/* Google Sign-in Button (hidden while pending) */}
          {!isPending && (
            <div className="mt-8">
              <button
                onClick={login}
                className="flex w-full items-center justify-center gap-3 bg-xtravirt-dark px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-xtravirt-navy"
                style={{ borderRadius: '4px', boxShadow: '3px 3px 0px 0px #E8AAEE' }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#fff"
                    fillOpacity="0.8"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#fff"
                    fillOpacity="0.9"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#fff"
                    fillOpacity="0.7"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#fff"
                    fillOpacity="0.85"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Sign in with Google
              </button>
            </div>
          )}

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-gray-400">Xtravirt Cloud Consulting</p>
        </div>
      </div>
    </div>
  );
}
